import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { relatorioEstagioService, estagioService } from '@/services/api';
import { RelatorioEstagio, Estagio } from '@/types';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';

// Schema de validação
const relatorioSchema = z.object({
  estagioId: z.number().min(1, 'Selecione um estágio'),
  tipo: z.enum(['Inicial', 'Parcial', 'Final'], {
    required_error: 'Selecione o tipo de relatório'
  }),
  prazoEntrega: z.string().min(1, 'Prazo de entrega é obrigatório'),
  dataEntregue: z.string().optional(),
  observacao: z.string().optional(),
});

type RelatorioFormData = Omit<RelatorioEstagio, 'id' | 'createdAt' | 'updatedAt'>;

export const NovoRelatorioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError } = useErrorHandler();
  const [loadError, setLoadError] = useState<string | null>(null);

  // Estados para o select com busca de estágio
  const [estagios, setEstagios] = useState<Estagio[]>([]);
  const [estagioQuery, setEstagioQuery] = useState<string>("");
  const [openEstagio, setOpenEstagio] = useState<boolean>(false);
  const estagioRef = useRef<HTMLDivElement | null>(null);

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<RelatorioFormData>({
    resolver: zodResolver(relatorioSchema),
    defaultValues: {
      estagioId: 0,
      tipo: 'Inicial',
      prazoEntrega: '',
      dataEntregue: '',
      observacao: '',
    },
    mode: 'onChange',
  });

  // Buscar estágios
  const { data: estagiosData } = useQuery({
    queryKey: ['estagios'],
    queryFn: () => estagioService.list({ page: 1, limit: 1000 }),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Carregar dados dos estágios para o select com busca
  useEffect(() => {
    if (estagiosData?.data) {
      setEstagios(estagiosData.data);
    }
  }, [estagiosData]);

  // Filtro para o select de estágio
  const filteredEstagios = estagios.filter(e =>
    !estagioQuery ||
    (e.aluno?.nome && e.aluno.nome.toLowerCase().includes(estagioQuery.toLowerCase())) ||
    (e.empresa?.nomeFantasia && e.empresa.nomeFantasia.toLowerCase().includes(estagioQuery.toLowerCase())) ||
    e.id.toString().includes(estagioQuery)
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: any) {
      const target = event.target;
      if (estagioRef.current && target && !estagioRef.current.contains(target)) {
        setOpenEstagio(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar relatório para edição
  const { data: relatorioData, isLoading: relatorioLoading, error: relatorioError } = useQuery<RelatorioEstagio>({
    queryKey: ['relatorio', id],
    queryFn: () => relatorioEstagioService.getById(parseInt(id!)),
    enabled: !!id,
    staleTime: 0, // Sempre buscar dados frescos para edição
  });

  useEffect(() => {
    if (relatorioError) {
      const message = handleError(relatorioError as any, 'Erro ao carregar relatório');
      setLoadError(message);
    }
  }, [relatorioError, handleError]);

  useEffect(() => {
    if (relatorioData) {
      setLoadError(null);
    }
  }, [relatorioData]);

  // Preencher formulário quando dados carregarem
  useEffect(() => {
    if (relatorioData && isEditing) {
      const dadosFormatados = {
        estagioId: relatorioData.estagioId,
        tipo: relatorioData.tipo,
        prazoEntrega: new Date(relatorioData.prazoEntrega).toISOString().split('T')[0],
        dataEntregue: relatorioData.dataEntregue
          ? new Date(relatorioData.dataEntregue).toISOString().split('T')[0]
          : '',
        observacao: relatorioData.observacao || '',
      };

      reset(dadosFormatados);

      // Preencher valor inicial do select de estágio com busca
      if (relatorioData.estagioId && estagios.length > 0) {
        const estagioSelecionado = estagios.find(e => e.id === relatorioData.estagioId);
        if (estagioSelecionado) {
          setEstagioQuery(`Estágio #${estagioSelecionado.id} - ${estagioSelecionado.aluno?.nome || 'Aluno não encontrado'}`);
        }
      }
    }
  }, [relatorioData, isEditing, reset, estagios]);

  // Mutation para criar relatório
  const createMutation = useMutation({
    mutationFn: relatorioEstagioService.create,
    onSuccess: () => {
      toast.success('Relatório criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['relatorios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      navigate('/relatorios');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Erro ao criar relatório';
      toast.error(errorMessage);
    },
  });

  // Mutation para atualizar relatório
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<RelatorioFormData> }) =>
      relatorioEstagioService.update(id, data),
    onSuccess: () => {
      toast.success('Relatório atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['relatorios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['relatorio', id] });
      }
      navigate('/relatorios');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar relatório';
      toast.error(errorMessage);
    },
  });

  // Submit handler
  const onSubmit = async (data: RelatorioFormData) => {
    try {
      setIsSubmitting(true);

      // Enviar as datas como strings YYYY-MM-DD (sem timezone) para evitar voltar 1 dia
      const formattedData = {
        ...data,
        prazoEntrega: data.prazoEntrega, // já está como YYYY-MM-DD pelo input type="date"
        dataEntregue: data.dataEntregue ? data.dataEntregue : undefined,
      } as any;

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: parseInt(id!),
          data: formattedData
        });
      } else {
        await createMutation.mutateAsync(formattedData);
      }
    } catch (error) {
      // Error is handled by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Editar Relatório</h1>
            <p className="mt-1 text-sm text-gray-500">{loadError}</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/relatorios')}>
            <ArrowLeftIcon className="h-5 w-5 mr-2" /> Voltar
          </Button>
        </div>
      </div>
    );
  }

  if (isEditing && relatorioLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados do relatório...</p>
        </div>
      </div>
    );
  }

  const tiposRelatorio = [
    { value: 'Inicial', label: 'Relatório Inicial', description: 'Primeiro relatório do estágio' },
    { value: 'Parcial', label: 'Relatório Parcial', description: 'Relatório intermediário' },
    { value: 'Final', label: 'Relatório Final', description: 'Relatório conclusivo do estágio' }
  ];

  // Lista derivada não utilizada removida para evitar aviso do TS

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/relatorios')}
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Voltar
        </Button>

        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DocumentTextIcon className="h-8 w-8 text-primary-600" />
            {isEditing ? 'Editar Relatório' : 'Novo Relatório'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing
              ? 'Edite as informações do relatório de estágio'
              : 'Cadastre um novo relatório de estágio no sistema'
            }
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados do Relatório */}
        <Card>
          <CardHeader>
            <CardTitle>Dados do Relatório</CardTitle>
            <CardDescription>
              Informações básicas do relatório de estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Estágio e Tipo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="estagioId" className="text-sm font-medium">
                  Estágio *
                </Label>
                <div className="relative" ref={estagioRef}>
                  <Input
                    id="estagioId"
                    type="text"
                    value={estagioQuery}
                    onChange={(e) => {
                      setEstagioQuery(e.target.value);
                      setOpenEstagio(true);
                    }}
                    onFocus={() => setOpenEstagio(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setOpenEstagio(false);
                      }
                    }}
                    placeholder="Digite para buscar estágio..."
                    className={`w-full ${errors.estagioId ? 'border-red-500' : ''}`}
                  />
                  {estagioQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setEstagioQuery('');
                        setValue('estagioId', 0);
                        setOpenEstagio(false);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                  {openEstagio && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredEstagios.length > 0 ? (
                        filteredEstagios.map((estagio) => (
                          <div
                            key={estagio.id}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setValue('estagioId', estagio.id);
                              setEstagioQuery(`Estágio #${estagio.id} - ${estagio.aluno?.nome || 'Aluno não encontrado'}`);
                              setOpenEstagio(false);
                            }}
                          >
                            <div className="font-medium">Estágio #{estagio.id}</div>
                            <div className="text-sm text-gray-600">
                              Aluno: {estagio.aluno?.nome || 'Não informado'}
                            </div>
                            {estagio.empresa?.nomeFantasia && (
                              <div className="text-sm text-gray-600">
                                Empresa: {estagio.empresa.nomeFantasia}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500">
                          Nenhum estágio encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.estagioId && (
                  <p className="text-sm text-red-600">{errors.estagioId.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo" className="text-sm font-medium">
                  Tipo de Relatório *
                </Label>
                <Controller
                  name="tipo"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      id="tipo"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.tipo ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Selecione o tipo</option>
                      {tiposRelatorio.map((tipo) => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.tipo && (
                  <p className="text-sm text-red-600">{errors.tipo.message}</p>
                )}
              </div>
            </div>

            {/* Datas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="prazoEntrega" className="text-sm font-medium">
                  Prazo de Entrega *
                </Label>
                <Controller
                  name="prazoEntrega"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="prazoEntrega"
                      type="date"
                      className={errors.prazoEntrega ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.prazoEntrega && (
                  <p className="text-sm text-red-600">{errors.prazoEntrega.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataEntregue" className="text-sm font-medium">
                  Data de Entrega
                </Label>
                <Controller
                  name="dataEntregue"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="dataEntregue"
                      type="date"
                      className={errors.dataEntregue ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.dataEntregue && (
                  <p className="text-sm text-red-600">{errors.dataEntregue.message}</p>
                )}
                <p className="text-xs text-gray-500">
                  Deixe em branco se ainda não foi entregue
                </p>
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacao" className="text-sm font-medium">
                Observações
              </Label>
              <Controller
                name="observacao"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    id="observacao"
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.observacao ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Digite observações sobre o relatório..."
                  />
                )}
              />
              {errors.observacao && (
                <p className="text-sm text-red-600">{errors.observacao.message}</p>
              )}
              <p className="text-xs text-gray-500">
                Máximo 500 caracteres
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Informações do Tipo Selecionado */}
        {watch('tipo') && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">
                    {tiposRelatorio.find(t => t.value === watch('tipo'))?.label}
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {tiposRelatorio.find(t => t.value === watch('tipo'))?.description}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/relatorios')}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting || !isDirty}
            className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{isEditing ? 'Salvando...' : 'Criando...'}</span>
              </div>
            ) : (
              isEditing ? 'Salvar Alterações' : 'Criar Relatório'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};